import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Actions, Select, Store, ofActionDispatched } from '@ngxs/store';
import { Article } from 'app/library/article/models/article.model';
import { ArticleService } from 'app/library/article/services/article.service';
import { CreateValidScheduledStreamContentBulkSuccess } from 'app/library/scheduled-stream-content/actions';
import { IScheduleStreamContentBulkActionPayload } from 'app/library/scheduled-stream-content/interfaces/schedule-stream-content-bulk.action-payload.interface';
import { ScheduledStreamContentService } from 'app/library/scheduled-stream-content/services/scheduled-stream-content.service';
import { IPostingItemPicked } from '../../interfaces/posting-item-picked.interface';
import { ContentTypeEnum } from 'app/library/widgets/models/content-type.enum';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { fuseAnimations } from 'app/projects/fuse/src/lib/animations';
import { ILanguageService } from 'app/projects/shared/src/lib/interfaces/language.interface';
import { MAXBRAIN_LANGUAGE_SERVICE } from 'app/projects/shared/src/lib/services/language.token';
import { BehaviorSubject, Observable, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { SearchAllPostings, UnsetAllPostings } from '../../actions/all-postings.action';
import { PostingType } from '../../models/posting-type.model';
import { Posting } from '../../models/posting.model';
import { PostingTypeApiService } from '../../services/posting-type.api-service';
import { PostingService } from '../../services/posting.service';
import { AllPostingsState } from '../../states/all-postings.state';
import { EngageNewPostingDialogComponent } from '../dialogs/engage-new-posting-dialog/engage-new-posting-dialog.component';

type RemovePostingAction = Posting | { clear: true };

@Component({
    selector: 'app-select-posting-page',
    templateUrl: './select-posting-page.component.html',
    styleUrls: ['./select-posting-page.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
})
export class SelectPostingPageComponent extends UnsubscribeOnDestroy implements OnInit, OnDestroy {
    @Select(AllPostingsState.getEntities)
    postings$: Observable<Posting[]>;

    @Select(AllPostingsState.getSearchTerm)
    postingsStateSearchTerm$: Observable<string>;

    @Select(AllPostingsState.getTotalCount)
    postingsStateTotalCount$: Observable<number>;

    @Select(AllPostingsState.isFetchingList)
    isFetchingPostings$: Observable<boolean>;

    @Input()
    audienceId: string;

    @Input()
    featuredStreamContentId: string;

    @Input()
    selectedContentTypeId: number;

    @Input()
    articleId: string;

    @Input()
    existingRelatedPostings: Posting[] = [];

    @Input()
    pickOnly = false;

    @Input()
    onlyValid = false;

    get isArticleContentType(): boolean {
        return Number(this.selectedContentTypeId) === ContentTypeEnum.Article;
    }

    get isArticleSelectionMode(): boolean {
        return this.isArticleContentType && !!this.articleId;
    }

    @Output()
    closeDialog = new EventEmitter<boolean | { closeParentDialog?: boolean; result?: Article }>();

    @Output()
    itemPicked = new EventEmitter<IPostingItemPicked>();

    searchInput = new FormControl();
    postingTypes: PostingType[];
    postingList: Posting[] = [];
    ContentTypeEnum = ContentTypeEnum;

    isLoading = false;

    private _loadPostings$ = new BehaviorSubject<boolean>(null);
    loadPostings$ = this._loadPostings$.asObservable();

    private _removePostingFromList = new BehaviorSubject<RemovePostingAction>(null);
    removePostingFromList$ = this._removePostingFromList.asObservable();

    languageInput = new FormControl();
    selectedLanguageId: string;

    postingTypeInput = new FormControl();
    selectedPostingTypeId: number;

    engageNewPostingDialogRef: MatDialogRef<EngageNewPostingDialogComponent>;

    constructor(
        @Inject(MAXBRAIN_LANGUAGE_SERVICE) public languageService: ILanguageService,
        private _store: Store,
        private _action$: Actions,
        private _matDialog: MatDialog,
        private _postingTypeApiService: PostingTypeApiService,
        private _matSnackBar: MatSnackBar,
        private _translateService: TranslateService,
        private _featuredStreamContentService: ScheduledStreamContentService,
        private _postingService: PostingService,
        private _articleService: ArticleService,
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        if (!this.isArticleContentType) {
            this.postingTypes = await this._postingTypeApiService.getPostingTypes();
        }

        this.postingTypeInput.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((postingTypeId: number) => {
            this.selectedPostingTypeId = postingTypeId;
            setTimeout(() => {
                this._loadPostings$.next(true);
            });
        });

        this.languageInput.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((selectedLanguageId: string) => {
            this.selectedLanguageId = selectedLanguageId;
            setTimeout(() => {
                this._loadPostings$.next(true);
            });
        });

        this.searchInput.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(300), distinctUntilChanged()).subscribe((searchValue: string) => {
            this._store.dispatch(new SearchAllPostings(searchValue));
        });

        this._action$
            .pipe(ofActionDispatched(CreateValidScheduledStreamContentBulkSuccess), takeUntil(this._unsubscribeAll))
            .subscribe((action: CreateValidScheduledStreamContentBulkSuccess) => {
                const bulk = action.payload;

                const createdCount = bulk.created.length;
                const failedCount = bulk.failed.length;

                let message: string;

                if (createdCount > 0) {
                    message = this._translateService.instant('ENGAGE.SCHEDULED_STREAM_CONTENT.MESSAGE.SCHEDULE_SUCCESS', {
                        createdCount,
                        failedCount,
                    });
                } else {
                    message = this._translateService.instant('ENGAGE.SCHEDULED_STREAM_CONTENT.MESSAGE.SCHEDULE_FAILED');
                }

                const errorTranslationMap: Record<string, string> = {
                    time_interval_overlaps_with_another_ScheduledStreamContent_interval: 'ENGAGE.SCHEDULED_STREAM_CONTENT.MESSAGE.SCHEDULE_FAILED.ERROR',
                };

                const groupedErrors = bulk.failed.reduce(
                    (acc, item) => {
                        const msg = item.message;

                        if (!acc[msg]) {
                            acc[msg] = 0;
                        }
                        acc[msg] += 1;

                        return acc;
                    },
                    {} as Record<string, number>,
                );

                const errorLines = Object.entries(groupedErrors)
                    .map(([msg, count]) => `${this._translateService.instant(errorTranslationMap[msg] || msg)} (${count} items)`)
                    .join('\n');

                if (failedCount > 0) {
                    message += `\n\n${errorLines}`;
                }

                this._matSnackBar.open(message, 'OK', {
                    verticalPosition: 'top',
                    duration: 5000,
                });

                this.isLoading = false;
                this.closeDialog.emit();
            });
    }

    async openDialogEngageNewPosting(): Promise<void> {
        const postingTypes = await this._postingTypeApiService.getPostingTypes('normal');
        const postingType = postingTypes.find((postingType) => postingType.name === 'event');
        this.engageNewPostingDialogRef = this._matDialog.open(EngageNewPostingDialogComponent, {
            disableClose: false,
            width: '740px',
        });
        this.engageNewPostingDialogRef.componentInstance.postingType = postingType;
        this.engageNewPostingDialogRef
            .afterClosed()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(async (dialogPosting: Posting) => {
                if (dialogPosting) {
                    const postingCreated = await this._postingService.createPosting(dialogPosting);
                    if (postingCreated) {
                        this._loadPostings$.next(true);
                    }
                }

                this.engageNewPostingDialogRef = null;
            });
    }

    onClearSelection(): void {
        this.postingList = [];
        this._removePostingFromList.next({ clear: true });
    }

    removeFromList(posting: Posting): void {
        this.postingList = [...this.postingList.filter((item) => item.id !== posting.id)];

        this._removePostingFromList.next(posting);
    }

    updateList(list: Posting[]): void {
        this.postingList = list;
    }

    async save(): Promise<void> {
        this.isLoading = true;

        if (this.pickOnly) {
            const first = this.postingList[0];
            const translation = first?.translations?.[0];

            if (first) {
                this.itemPicked.emit({
                    postingId: first.id ? Number(first.id) : null,
                    url: first.url || null,
                    title: translation?.title || '',
                    description: translation?.description || '',
                    image: first.image ?? null,
                });
            }

            this.isLoading = false;
            return;
        }

        if (this.isArticleSelectionMode) {
            const relatedPostings = [...this.existingRelatedPostings, ...this.postingList].reduce((acc, posting) => {
                if (!acc.some((item) => item.id === posting.id)) {
                    acc.push(posting);
                }

                return acc;
            }, [] as Posting[]);
            const updatedArticle = await this._articleService.updateArticleRelatedPostings(this.articleId, relatedPostings);
            this.isLoading = false;

            if (updatedArticle) {
                this.closeDialog.emit({ closeParentDialog: false, result: updatedArticle });
            }
            return;
        }

        const data: IScheduleStreamContentBulkActionPayload = {
            audience: { id: this.audienceId },
            featuredStream: { id: this.featuredStreamContentId },
            postingIds: this.postingList.map((posting) => Number(posting.id)),
        };

        this._featuredStreamContentService.createScheduledStreamContentBulk(data);
    }

    ngOnDestroy(): void {
        this._store.dispatch(new UnsetAllPostings());
        super.ngOnDestroy();
    }
}
